import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import {
  filterMockTenantUsers,
  MOCK_TENANT_USERS,
  MOCK_TENANT_USERS_LATENCY_MS,
  type TenantUsersScenario,
} from "./tenantUsersMock";
import type { TenantUserSummary, TenantUsersSearchQuery } from "./tenantUsers.types";

// Toggle: REACT_APP_TENANT_USERS_API=mock → offline fixtures
const isMockTenantUsersApi = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_TENANT_USERS_API);

const mockScenario = (): TenantUsersScenario =>
  (process.env.REACT_APP_TENANT_USERS_SCENARIO as TenantUsersScenario) ??
  "default";

const settle = <T>(value: T): Promise<T> =>
  new Promise((resolve) =>
    setTimeout(() => resolve(value), MOCK_TENANT_USERS_LATENCY_MS),
  );

/** Mirrors the interceptor's error shape so `status` branches behave as in live mode. */
const failLikeBackend = (status: number, message: string): Promise<never> =>
  new Promise((_resolve, reject) =>
    setTimeout(
      () => reject(Object.assign(new Error(message), { status })),
      MOCK_TENANT_USERS_LATENCY_MS,
    ),
  );

/**
 * Searches active members of one tenant by email/first name/last name, for
 * the story-invite picker. `tenantId` must be one of the caller's own active
 * tenant memberships — a 404 otherwise, same as the sibling workspaces
 * endpoint (`fetchTenantWorkspaces` in `services/tenantContext`).
 */
export const searchTenantUsers = (
  tenantId: string,
  query: TenantUsersSearchQuery = {},
): Promise<TenantUserSummary[]> => {
  if (isMockTenantUsersApi()) {
    const scenario = mockScenario();
    if (scenario === "error") {
      return failLikeBackend(500, "Tenant directory is unavailable (mock).");
    }
    const matches =
      scenario === "empty"
        ? []
        : filterMockTenantUsers(MOCK_TENANT_USERS, query.search);
    return settle(
      query.limit != null ? matches.slice(0, query.limit) : matches,
    );
  }
  return apiService.get<TenantUserSummary[]>(
    API_ENDPOINTS.tenants.users(tenantId),
    { search: query.search, limit: query.limit },
  );
};
